import { useCallback } from "react";

import { ApolloError, FetchResult, useMutation } from "@apollo/client";

import {
  DASHBOARDS,
  UPDATE_DASHBOARD,
  WORKSPACE,
  WORKSPACES,
  WorkspaceClientName
} from "api/workspace";

import { DashboardUpdate } from "models/workspace";

import { useWorkspaceContext } from "components/workspace/hooks/useWorkspaceContext";

interface UseRenameDashboardOptions {
  onSuccess?: () => void;
  onError?: (error: ApolloError) => void;
}

export type UseRenameDashboardReturnT = {
  mutate: (dashboardId: string, newTitle: string) => Promise<FetchResult | undefined>;
  isLoading: boolean;
};

export function useRenameDashboard({
  onSuccess,
  onError
}: UseRenameDashboardOptions = {}): UseRenameDashboardReturnT {
  const { workspace } = useWorkspaceContext();
  const [updateDashboard, { loading }] = useMutation(UPDATE_DASHBOARD);

  const mutate = useCallback(
    async (dashboardId: string, newTitle: string) => {
      if (!dashboardId || !newTitle || !workspace) return;

      // Map to the update dashboard model.
      const updatedDashboard: DashboardUpdate = {
        dashboardId: dashboardId,
        title: newTitle
      };

      return updateDashboard({
        variables: {
          dashboard: updatedDashboard
        },
        context: {
          clientName: WorkspaceClientName
        },
        refetchQueries: [
          { query: DASHBOARDS, context: { clientName: WorkspaceClientName } },
          { query: WORKSPACES, context: { clientName: WorkspaceClientName } },
          {
            query: WORKSPACE,
            variables: { id: workspace.workspaceId },
            context: { clientName: WorkspaceClientName }
          }
        ],
        onCompleted: () => {
          onSuccess?.();
        },
        onError: (error: ApolloError) => {
          // eslint-disable-next-line no-console
          console.error(error.message, error);
          onError?.(error);
        }
      });
    },
    [updateDashboard, onSuccess, onError, workspace]
  );

  return { mutate, isLoading: loading };
}
